import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, Loader2, Mail, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";

const fmt = (v) => { const d = new Date(v); return isNaN(d) ? "" : `${d.toLocaleDateString("it-IT")} ${d.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}`; };

function PendingItem({ m, busy, onConfirm, onOpen, onIgnore }) {
  return (
    <li className="px-3 py-2.5 border-b border-slate-800 last:border-0" data-testid={`bell-item-${m.id}`}>
      <div className="flex items-center gap-1.5 text-[11px] text-slate-500">
        {m.account === "pec" ? <ShieldCheck className="h-3 w-3" /> : <Mail className="h-3 w-3" />}
        <span className="uppercase">{m.account}</span><span className="ml-auto font-mono">{fmt(m.date)}</span>
      </div>
      <button type="button" onClick={() => onOpen(m)} className="block w-full text-left mt-0.5" data-testid={`bell-item-open-${m.id}`}>
        <div className="text-sm font-semibold truncate" style={{ color: "#f1f5f9" }}>{m.from_name || m.from_addr}</div>
        <div className="text-xs truncate" style={{ color: "#94a3b8" }}>{m.subject || "(senza oggetto)"}</div>
      </button>
      <div className="mt-1.5 space-y-1">
        {m.pratiche_suggerite.map((r) => (
          <div key={r.id} className="flex items-center gap-2 text-xs">
            <span className="truncate" style={{ color: "#cbd5e1" }}>Pratica {r.numero_pratica}{r.nome_assistito ? ` · ${r.nome_assistito}` : ""}</span>
            <button type="button" disabled={busy} onClick={() => onConfirm(m, r)} data-testid={`bell-confirm-${m.id}-${r.id}`}
              className="ml-auto shrink-0 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-2 py-0.5 transition-colors disabled:opacity-60">Conferma</button>
          </div>
        ))}
        <div className="flex justify-end">
          <button type="button" disabled={busy} onClick={() => onIgnore(m)} data-testid={`bell-ignore-${m.id}`}
            className="rounded-md border border-slate-600 px-2 py-0.5 text-xs font-medium hover:bg-slate-800 transition-colors disabled:opacity-60" style={{ color: "#cbd5e1" }}>Ignora</button>
        </div>
      </div>
    </li>
  );
}

export const SuggestionsBell = ({ count }) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/studio/mail/pending-suggestions").then(({ data }) => setItems(data || [])).catch(() => setItems([]));
  const onOpenChange = (o) => { setOpen(o); if (o) { setItems(null); load(); } };
  const confirm = async (m, r) => {
    setBusy(true);
    try {
      await api.patch(`/studio/mail/${m.account}/messages/${m.id}/pratica`, { pratica_id: r.id, numero_pratica: r.numero_pratica });
      toast.success(`Email collegata alla pratica ${r.numero_pratica || ""}`.trim());
      setItems((xs) => (xs || []).filter((x) => x.id !== m.id));
      window.dispatchEvent(new Event("pgp-mail-link-changed"));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const ignore = async (m) => {
    setBusy(true);
    try {
      await api.patch(`/studio/mail/${m.account}/messages/${m.id}/suggestion/dismiss`);
      toast.success("Suggerimento ignorato");
      setItems((xs) => (xs || []).filter((x) => x.id !== m.id));
      window.dispatchEvent(new Event("pgp-mail-unread-changed"));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const openMsg = (m) => { setOpen(false); navigate(`/app/${m.account === "pec" ? "pec" : "email"}`); };

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <button type="button" data-testid="nav-bell-suggestions" title={`${count} email in arrivo da confermare`}
          className="ml-auto relative h-9 w-9 flex items-center justify-center rounded-lg text-amber-400 hover:bg-amber-500/10 transition-colors">
          <Bell className="h-5 w-5" />
          <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 flex items-center justify-center text-[9px] font-bold font-mono bg-amber-500 text-[#0a0d14] rounded-full" data-testid="nav-bell-count">{count}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" side="right" className="w-80 p-0 bg-slate-900 border-slate-700 text-slate-200" data-testid="bell-popover">
        <div className="px-3 py-2 border-b border-slate-800 text-sm font-semibold" style={{ color: "#f1f5f9" }}>Email da confermare</div>
        {items === null ? (
          <div className="py-6 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-slate-500" /></div>
        ) : !items.length ? (
          <p className="py-6 text-center text-sm text-slate-500" data-testid="bell-empty">Nessuna email in attesa di conferma</p>
        ) : (
          <ul className="max-h-96 overflow-y-auto">{items.map((m) => <PendingItem key={m.id} m={m} busy={busy} onConfirm={confirm} onOpen={openMsg} onIgnore={ignore} />)}</ul>
        )}
      </PopoverContent>
    </Popover>
  );
};
